<template>
    <VueEditor
        id="field-post_content"
        :name="fieldName"
        placeholder="Start writing..."
        v-model="value"
        ref="editor"
        required
        :editorOptions="editorOptions"
        @focus="handleFocus"
        @blur="handleBlur"
    />
</template>
<script>
import { VueEditor } from 'vue2-editor';
import API from '@api';
import '../../lib/vue2-editor-quill-mention/quill.mention';
import '../../lib/vue2-editor-plain-clipboard/quill.plain-clipboard';
import '../../lib/vue2-editor-auto-link/quill.auto-link';

export default {
    name: 'TextareaWithMentions',
    inject: {
        $validator: '$validator',
    },
    $_veeValidate: {
        value() {
            return this.selected;
        },
        name() {
            return this.name;
        },
    },
    components: {
        VueEditor,
    },
    props: {
        defaultValue: {
            type: String,
            default: null,
        },
        fieldName: {
            type: String,
            default: 'post_content',
        },
    },
    data() {
        return {
            value: this.defaultValue ?? '',
            mentionItems: [],
            editorOptions: {
                selectKeys: [],
                modules: {
                    toolbar: false,
                    autolink: true,
                    mention: {
                        allowedChars: /^[A-Za-z\s]*$/,
                        mentionDenotationChars: ['@', '#'],
                        renderItem: (data) => {
                            if(data.disabled) {
                                const div = document.createElement('div');
                                div.style =
                                    'height:10px;line-height:10px;font-size:10px;background-color:#ccc;margin:0 -20px;padding:4px';
                                div.innerText = data.value;
                                return div;
                            }
                            return data.value;
                        },
                        renderLoading: () => {
                            const parsedSpinner = new DOMParser().parseFromString(
                                `
                                <svg height="24" width="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z"
                                        class="spinner"
                                        fill="#25C8C1"
                                    />
                                </svg>
                            `,
                                'text/html',
                            );

                            return parsedSpinner.body.firstChild;
                        },
                        source: async function(searchTerm, renderList, mentionChar) {
                            try {
                                if(searchTerm.length === 0) {
                                    renderList([], searchTerm);
                                } else {
                                    const users = await API.getUsers({query: searchTerm});
                                    const matches = Array.from(users.data).map((user) => ({
                                        id: user.id,
                                        value: user.name,
                                        data: user,
                                    }));

                                    renderList(matches, searchTerm);
                                }
                            } catch(err) {
                                console.warn(err);
                            }
                        },
                    },
                },
            },
        }
    },
    methods: {
        async loadIssues(searchText = null) {
            this.$wait.start('loading-mentions');
            try {
                const users = await API.getUsers({query: searchText});
                this.mentionItems = Array.from(users.data).map((user) => ({
                    value: user.name,
                    label: user.name,
                    data: user,
                }));
            } catch(err) {
                this.mentionItems = [];
            } finally {
                this.$wait.end('loading-mentions');
            }
        },
        handleFocus(ev) {
            const el = this.$refs.editor.quill.container.querySelector('.ql-editor');
            if(el) {
                el.style.minHeight = `7.6rem`;
            }
        },
        handleBlur(ev) {
            const el = this.$refs.editor.quill.container.querySelector('.ql-editor');
            if(el) {
                el.style.minHeight = `0`;
            }
        },
        initials: function(name) {
            return name ? name.split(' ').map((namePart) => namePart[0]).join('') : '';
        },
        reset: function() {
            this.value = null;
        },
    },
    watch: {
        value: function(val, oldVal) {
            this.$emit('input', val);
        },
    },
}
</script>
<style lang="scss">
@import '~@/theme/_variables.scss';
@import "~vue2-editor/dist/vue2-editor.css";
@import '~quill/dist/quill.core.css';
@import '~quill/dist/quill.snow.css';

.card--user {
    cursor: pointer;
}

.quillWrapper {
    width: 100%;

    .ql-container {
        border: none !important;
    }

    .ql-editor {
        background-color: #F1F1F1;
        border-radius: 1.8rem 0 1.8rem 1.8rem;
        min-height: 3.6rem;
        padding: .8rem 1.5rem;

        a {
            text-decoration: none !important;
        }

        p,
        &.ql-blank:before {
            font-style: normal;
            font-family: "Axiforma", sans-serif;
            font-weight: 300;
            font-size: 1.4rem;
        }
    }

    .ql-mention-list-container {
        background-color: #FFFFFF;
        border-radius: .4rem;
        box-shadow: 0 .6rem 2rem 0 rgba(49, 49, 74, 0.05);
        overflow: auto;
        width: 27rem;
        z-index: 9001;
    }

    .ql-mention-loading {
        font-size: 1.4rem;
        line-height: 4.4rem;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem 2rem;
    }

    .ql-mention-list {
        list-style: none;
        margin: 0;
        overflow: hidden;
        padding: 1rem 0;

        &:empty {
            display: none !important;
        }
    }

    .ql-mention-list-item {
        cursor: pointer;
        line-height: 3.2rem;
        padding: 0 2rem;
        vertical-align: middle;
        font-family: "Axiforma", sans-serif;
        font-weight: 300;
        font-size: 1.4rem;
    }

    .ql-mention-list-item.disabled {
        cursor: auto
    }

    .ql-mention-list-item.selected {
        background-color: #FAFAFA;
        text-decoration: none;
    }

    .mention {
        color: $link;
        user-select: all;
        width: 6.5rem;
    }

    .mention > span {
        margin: 0 3px
    }
}
</style>
